import api from './api';

/**
 * Apontamentos registrados pelo app (ESF-API, módulo `apontamento`).
 *
 * ATENÇÃO ao caminho: o baseURL desta web é a RAIZ da API, não o
 * /api-essencial/v1 — cada chamada escreve o prefixo, como no resto do projeto.
 *
 * O apontamento aqui é o REGISTRO do que o vendedor deu baixa: a baixa em si
 * acontece no Protheus (incluietq), direto do app.
 */

const PREFIXO = '/api-essencial/v1';

export type EtapaAnexo = 'AGENDAMENTO' | 'APONTAMENTO';

export interface EtiquetaApontada {
  apontamento_etiqueta_id?: string;
  codigo: string;
  produto_codigo?: string | null;
  produto_descricao?: string | null;
  lote?: string | null;
  validade?: string | null;
  quantidade?: string | number | null;
  data_leitura?: string | null;
}

export interface AnexoCirurgia {
  cirurgia_anexo_id: string;
  etapa: EtapaAnexo;
  nome_original?: string | null;
  tipo_arquivo: string;
  descricao?: string | null;
  ordem: number;
  data_cadastro?: string | null;
}

export interface Apontamento {
  apontamento_id: string;
  agendamento_id?: string | null;
  usuario_id: string;
  cliente_id?: string | null;
  filial: string;
  paciente?: string | null;
  medico_nome?: string | null;
  convenio_nome?: string | null;
  data_procedimento?: string | null;
  /** Observação do vendedor para o faturamento. Ausente enquanto a API não devolver o campo. */
  observacoes?: string | null;
  data_cadastro?: string | null;
  clientes?: { fantasia?: string | null; razao_social?: string | null } | null;
  usuarios?: { usuario_id: string; name?: string | null } | null;
  etiquetas?: EtiquetaApontada[];
  anexos?: AnexoCirurgia[];
  _count?: { etiquetas: number; anexos: number };
}

/** O módulo ainda não subiu na API deste ambiente. */
export class ModuloApontamentosIndisponivelError extends Error {
  constructor() {
    super('O registro de apontamentos ainda não está disponível nesta API.');
    this.name = 'ModuloApontamentosIndisponivelError';
  }
}

function tratarErro(erro: any): never {
  // 404/405/501 sem corpo = rota não publicada. Um 404 COM corpo é resposta de
  // negócio ("apontamento não encontrado") e segue como erro normal.
  const status = erro?.response?.status;
  if ((status === 404 || status === 405 || status === 501) && !erro?.response?.data?.message) {
    throw new ModuloApontamentosIndisponivelError();
  }
  throw erro;
}

interface FiltroApontamentos {
  filial?: string;
  desde?: string;
}

/** Todos os apontamentos do período — a visão do faturamento. */
export async function listarApontamentos(filtro: FiltroApontamentos = {}): Promise<Apontamento[]> {
  try {
    const { data } = await api.get<Apontamento[]>(`${PREFIXO}/apontamentos`, { params: filtro });
    return Array.isArray(data) ? data : [];
  } catch (erro) {
    return tratarErro(erro);
  }
}

/**
 * Quem registrou o apontamento. O `usuarios` aninhado é a fonte; `usuario_id`
 * cobre a resposta que vier sem ele.
 */
export function usuarioDoApontamento(apontamento: Apontamento): string {
  return apontamento.usuarios?.usuario_id || apontamento.usuario_id;
}

export interface ApontamentosDaPessoa {
  usuarioId: string;
  nome: string;
  total: number;
  /** Soma das etiquetas lidas nos apontamentos dessa pessoa. */
  etiquetas: number;
}

/**
 * Quantos apontamentos cada pessoa registrou, do maior para o menor.
 *
 * Agrupa por `usuario_id`, não pelo nome: dois vendedores homônimos são pessoas
 * diferentes e não podem somar na mesma barra.
 */
export function contarApontamentosPorPessoa(apontamentos: Apontamento[]): ApontamentosDaPessoa[] {
  const porUsuario = new Map<string, ApontamentosDaPessoa>();

  apontamentos.forEach((apontamento) => {
    const usuarioId = usuarioDoApontamento(apontamento);
    const pessoa = porUsuario.get(usuarioId) || {
      usuarioId,
      nome: '',
      total: 0,
      etiquetas: 0,
    };

    pessoa.nome = pessoa.nome || String(apontamento.usuarios?.name || '').trim();
    pessoa.total += 1;
    pessoa.etiquetas += apontamento._count?.etiquetas ?? 0;
    porUsuario.set(usuarioId, pessoa);
  });

  return Array.from(porUsuario.values())
    .map((pessoa) => ({ ...pessoa, nome: pessoa.nome || 'Sem nome' }))
    .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Maiúsculas, sem acento e com um espaço só — o nome do paciente é digitado à mão. */
const normalizarNome = (nome?: string | null): string =>
  String(nome || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const chaveAvulso = (clienteId: string, paciente: string) => `${clienteId}|${paciente}`;

export interface IndiceApontamentos {
  /** agendamento_id → apontamentos feitos a partir dele. */
  porAgendamento: Map<string, string[]>;
  /** Avulsos: `cliente_id|PACIENTE` → apontamentos, com o dia (UTC) do registro. */
  avulsos: Map<string, { apontamentoId: string; dia: string }[]>;
}

/**
 * Organiza os apontamentos para o Mapa Cirúrgico achar os de cada pedido.
 *
 * O vínculo certo é o `agendamento_id`, mas a maioria é AVULSA: o vendedor
 * aponta sem partir da agenda e digita hospital e paciente. Esses só se ligam a
 * um pedido pelo par hospital + paciente (ver `apontamentosDoPedido`).
 */
export function indexarApontamentos(apontamentos: Apontamento[]): IndiceApontamentos {
  const indice: IndiceApontamentos = { porAgendamento: new Map(), avulsos: new Map() };

  (Array.isArray(apontamentos) ? apontamentos : []).forEach((apontamento) => {
    if (apontamento.agendamento_id) {
      const lista = indice.porAgendamento.get(apontamento.agendamento_id) || [];
      lista.push(apontamento.apontamento_id);
      indice.porAgendamento.set(apontamento.agendamento_id, lista);
      return;
    }

    const paciente = normalizarNome(apontamento.paciente);
    const dia = String(apontamento.data_cadastro || '').substring(0, 10);
    if (!apontamento.cliente_id || !paciente || !dia) return;

    const chave = chaveAvulso(apontamento.cliente_id, paciente);
    const lista = indice.avulsos.get(chave) || [];
    lista.push({ apontamentoId: apontamento.apontamento_id, dia });
    indice.avulsos.set(chave, lista);
  });

  return indice;
}

export interface PedidoParaApontamento {
  agendamentoIds: string[];
  clienteId?: string | null;
  paciente?: string | null;
  /** `data_emissao` do pedido (ISO). */
  dataEmissao?: string | null;
}

/**
 * Apontamentos de um pedido (ids); lista vazia = ainda não foi apontado.
 *
 * O avulso entra quando é do mesmo hospital e do mesmo paciente — nome completo
 * e idêntico, para um "MARIA" solto não casar com outra pessoa — e foi
 * registrado do dia da emissão em diante: apontamento ANTERIOR ao pedido é de
 * outra cirurgia do mesmo paciente (ou o pedido é o de faturamento, digitado
 * depois). Nome com erro de digitação não casa; o pedido segue pelo status do ERP.
 */
export function apontamentosDoPedido(
  indice: IndiceApontamentos,
  pedido: PedidoParaApontamento
): string[] {
  const ids: string[] = [];
  pedido.agendamentoIds.forEach((agendamentoId) => {
    ids.push(...(indice.porAgendamento.get(agendamentoId) || []));
  });

  const paciente = normalizarNome(pedido.paciente);
  const emissao = String(pedido.dataEmissao || '').substring(0, 10);
  if (pedido.clienteId && paciente.includes(' ') && emissao) {
    (indice.avulsos.get(chaveAvulso(pedido.clienteId, paciente)) || [])
      .filter((avulso) => avulso.dia >= emissao)
      .forEach((avulso) => ids.push(avulso.apontamentoId));
  }

  return ids;
}

/** Detalhe com os materiais apontados e os anexos. */
export async function buscarApontamento(apontamentoId: string): Promise<Apontamento> {
  try {
    const { data } = await api.get<Apontamento>(`${PREFIXO}/apontamentos/${apontamentoId}`);
    return data;
  } catch (erro) {
    return tratarErro(erro);
  }
}

/**
 * Baixa um anexo como blob.
 *
 * A rota exige token, então a URL não serve direto num `<img src>`: quem exibe
 * cria uma object URL a partir do blob.
 */
export async function baixarAnexoApontamento(
  apontamentoId: string,
  anexoId: string
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${PREFIXO}/apontamentos/${apontamentoId}/anexos/${anexoId}`,
      { responseType: 'blob' }
    );
    return data;
  } catch (erro) {
    return tratarErro(erro);
  }
}
